/**
 * Project validation: the published schemas plus the rules that span files.
 * Used by the `animo-editor validate` command, by the dev server before it
 * accepts outside changes, and by agents checking their own edits.
 */
import { Ajv2020, type ValidateFunction } from 'ajv/dist/2020.js';
import { fileKindFor } from './canonical.js';
import { CAMERA_TARGET, CONNECTION_ACTIONS, readConnections } from './connections.js';
import { sceneSoundNames, soundsPathOf, type SoundsFile } from './sound.js';
import { CORE_TYPES, COMMON_FIELDS, DISPLAY_FIELDS, isCoreType, TYPE_FIELDS } from './constants.js';
import { listIds, walkObjects } from './ids.js';
import { FormatVersionError, migrate } from './migrate.js';
import { normalizeProjectPath, ProjectPathError, resolveFrom } from './paths.js';
import { findPrefabCycles, resolveObjects } from './prefab.js';
import { CLIP_FIELDS, isEventTrack, isFieldTrack, isPlayTrack, isSoundTrack, targetPath, type ClipsFile } from './clips.js';
import { clipsSchema, prefabSchema, projectSchema, sceneSchema, soundsSchema, themeSchema } from './schema.js';
import { colorRefs, isHexColor, styleRefs, styleSectionFor, THEME_SECTIONS, themePathOf, type ThemeFile } from './theme.js';
import { isPluginScript } from './script-source.js';
import { svgProblems } from './svg-check.js';
import { dataFileLinks } from './moves.js';
import { compressProblem } from './compress.js';
import type { BitmapTextObject, FilterEntry, PrefabFile, ProjectFile, SceneFile, SceneObject, SceneTilemapRef, SpriteObject, TilemapLayerObject, VideoObject } from './types.js';

export interface Problem {
  severity: 'error' | 'warning';
  /** Project path of the file the problem is in. */
  file: string;
  /** Object id, or a JSON path such as `/children/2`, when the problem is inside the file. */
  at?: string;
  message: string;
}

export interface ValidateInput {
  /** The parsed project.json. */
  project: unknown;
  /** Reads a file by project path (relative to the project root). Returns undefined when missing. */
  readFile: (path: string) => string | undefined;
  /**
   * Whether a script file exists, by path relative to project.json's folder.
   * When given, scripts attached to objects are checked.
   */
  scriptExists?: (path: string) => boolean;
  /** Extra prefab files to check even if no scene places them, such as every `*.prefab.json` on disk. */
  prefabs?: string[];
}

let compiled: { project: ValidateFunction; scene: ValidateFunction; prefab: ValidateFunction; clips: ValidateFunction; theme: ValidateFunction; sounds: ValidateFunction } | undefined;

function validators() {
  if (!compiled) {
    const ajv = new Ajv2020({ allErrors: true, strict: false });
    compiled = {
      project: ajv.compile(projectSchema),
      scene: ajv.compile(sceneSchema),
      prefab: ajv.compile(prefabSchema),
      clips: ajv.compile(clipsSchema),
      theme: ajv.compile(themeSchema),
      sounds: ajv.compile(soundsSchema),
    };
  }
  return compiled;
}

interface PackEntry {
  key: string;
  type: string;
  pack: string;
  /** Project paths of the files this entry loads, such as the atlas JSON. */
  urls: Record<string, string>;
}

const PACK_URL_FIELDS = ['url', 'textureURL', 'atlasURL', 'fontDataURL', 'normalMap', 'dataURL', 'jsonURL'];

class Checker {
  problems: Problem[] = [];
  private json = new Map<string, unknown | null>();
  packs = new Map<string, PackEntry[]>();
  textures = new Map<string, PackEntry>();
  /** Bitmap fonts by key. */
  fonts = new Map<string, PackEntry>();
  videos = new Map<string, PackEntry>();
  audio = new Map<string, PackEntry>();
  animationKeys = new Map<string, string>();
  prefabs = new Map<string, PrefabFile | null>();
  sceneKeys = new Map<string, string>();

  constructor(private input: ValidateInput) {}

  error(file: string, message: string, at?: string) {
    this.problems.push({ severity: 'error', file, message, ...(at !== undefined && { at }) });
  }

  warn(file: string, message: string, at?: string) {
    this.problems.push({ severity: 'warning', file, message, ...(at !== undefined && { at }) });
  }

  /** Normalises a path found in `file`, reporting it if invalid. */
  path(file: string, path: unknown, at?: string): string | undefined {
    if (typeof path !== 'string') return undefined;
    try {
      return normalizeProjectPath(path);
    } catch (e) {
      this.error(file, e instanceof ProjectPathError ? e.message : String(e), at);
      return undefined;
    }
  }

  readJson(path: string, from: string, what: string): unknown | undefined {
    if (this.json.has(path)) return this.json.get(path) ?? undefined;
    const text = this.input.readFile(path);
    if (text === undefined) {
      this.error(from, `${what} "${path}" does not exist`);
      this.json.set(path, null);
      return undefined;
    }
    try {
      const value = JSON.parse(text) as unknown;
      this.json.set(path, value);
      return value;
    } catch (e) {
      this.error(path, `Not valid JSON: ${(e as Error).message}`);
      this.json.set(path, null);
      return undefined;
    }
  }

  schema(file: string, fn: ValidateFunction, value: unknown): boolean {
    if (fn(value)) return true;
    for (const err of fn.errors ?? []) {
      // `if`/`then` wrappers repeat the real error; skip them.
      if (err.keyword === 'if') continue;
      this.error(file, `${err.instancePath || '/'} ${err.message ?? 'is invalid'}`, err.instancePath || undefined);
    }
    return false;
  }

  versioned<T>(file: string, value: unknown): T | undefined {
    try {
      return migrate<T>(value).file;
    } catch (e) {
      this.error(file, e instanceof FormatVersionError ? e.message : String(e));
      return undefined;
    }
  }

  loadPack(path: string, from: string) {
    if (this.packs.has(path)) return;
    const pack = this.readJson(path, from, 'Asset pack');
    const entries: PackEntry[] = [];
    this.packs.set(path, entries);
    if (pack === undefined) return;
    if (typeof pack !== 'object' || pack === null || Array.isArray(pack)) {
      this.error(path, 'An asset pack must be a JSON object of sections');
      return;
    }
    for (const [sectionName, section] of Object.entries(pack as Record<string, unknown>)) {
      if (sectionName === 'meta') continue;
      const s = section as { files?: unknown; path?: unknown; baseURL?: unknown };
      if (!s || !Array.isArray(s.files)) continue;
      if (typeof s.baseURL === 'string' && s.baseURL !== '') {
        this.warn(path, `Section "${sectionName}" sets baseURL; the editor can only check files under the project root`);
      }
      const base = typeof s.path === 'string' ? s.path.replace(/\/?$/, '/').replace(/^\/$/, '') : '';
      s.files.forEach((f: unknown, i: number) => {
        const file = f as Record<string, unknown>;
        if (typeof file.key !== 'string' || typeof file.type !== 'string') {
          this.error(path, `Section "${sectionName}" file ${i} needs a key and a type`);
          return;
        }
        if (file.compress !== undefined) {
          const problem = compressProblem(file.type, file.compress);
          if (problem) this.error(path, `"${file.key}": ${problem}`, `${sectionName}/${file.key}`);
        }
        if (file.filter !== undefined && file.filter !== 'pixel' && file.filter !== 'smooth') {
          this.error(path, `"${file.key}" has filter ${JSON.stringify(file.filter)}; use "pixel", "smooth", or leave it out for the game's default`, `${sectionName}/${file.key}`);
        }
        const urls: Record<string, string> = {};
        for (const field of PACK_URL_FIELDS) {
          // A sound or video in several formats lists one URL per format; each must exist.
          const list = Array.isArray(file[field]) ? (file[field] as unknown[]) : [file[field]];
          for (const url of list) {
            if (typeof url !== 'string' || /^(data:|blob:|https?:)/.test(url)) continue;
            const resolved = this.path(path, base + url, `${sectionName}/${file.key}`);
            if (!resolved) continue;
            urls[field] ??= resolved;
            if (this.input.readFile(resolved) === undefined) {
              this.error(path, `"${file.key}" points at ${resolved}, which does not exist`, `${sectionName}/${file.key}`);
            }
          }
        }
        const entry: PackEntry = { key: file.key, type: file.type, pack: path, urls };
        entries.push(entry);
        if (file.type === 'bitmapFont') {
          if (this.fonts.has(file.key)) this.error(path, `Bitmap font key "${file.key}" is defined more than once`, `${sectionName}/${file.key}`);
          else this.fonts.set(file.key, entry);
        }
        if (file.type === 'video') this.videos.set(file.key, entry);
        if (file.type === 'audio') this.audio.set(file.key, entry);
        // A bitmap font's page image is a texture under the font's key too.
        const textureTypes = ['image', 'spritesheet', 'atlas', 'multiatlas', 'aseprite', 'unityAtlas', 'atlasXML', 'svg', 'bitmapFont'];
        if (!textureTypes.includes(file.type)) return;
        const existing = this.textures.get(file.key);
        if (existing) {
          const where = existing.pack === path ? 'more than once in this pack' : `also in ${existing.pack}`;
          this.error(path, `Texture key "${file.key}" is defined ${where}. Phaser silently ignores all but the first.`, `${sectionName}/${file.key}`);
          return;
        }
        this.textures.set(file.key, entry);
        if (file.type === 'aseprite' && urls.atlasURL) {
          const data = this.readJson(urls.atlasURL, path, 'Aseprite data') as { meta?: { frameTags?: { name?: string }[] } } | undefined;
          for (const tag of data?.meta?.frameTags ?? []) {
            if (typeof tag.name === 'string' && !this.animationKeys.has(tag.name)) this.animationKeys.set(tag.name, urls.atlasURL);
          }
        }
      });
    }
  }

  /** SVG files already checked for what vector images can't draw. */
  private svgChecked = new Set<string>();

  /** A vector image needs an svg entry; what its SVG uses that won't draw is said once per file. */
  checkVectorTexture(file: string, id: string, tex: PackEntry): void {
    if (tex.type !== 'svg') {
      this.error(file, `Vector image texture "${tex.key}" is a ${tex.type} entry: it needs an "svg" entry in ${tex.pack}`, id);
      return;
    }
    const svgPath = tex.urls.url;
    if (!svgPath || this.svgChecked.has(svgPath)) return;
    this.svgChecked.add(svgPath);
    const text = this.input.readFile(svgPath);
    // Some callers only know that a non-JSON file exists, and give it as empty.
    if (!text) return;
    const problems = svgProblems(text);
    if (problems.length) this.warn(file, `${svgPath} uses ${problems.join(', ')}, which vector images don't draw as they look elsewhere`, id);
  }

  /** Frame names of an atlas texture, or undefined when they can't be known. */
  frameNames(entry: PackEntry): Set<string> | undefined {
    if (entry.type !== 'atlas' && entry.type !== 'aseprite') return undefined;
    const atlasPath = entry.urls.atlasURL;
    if (!atlasPath) return undefined;
    const data = this.readJson(atlasPath, entry.pack, 'Atlas data') as
      | { frames?: unknown; textures?: { frames?: { filename?: string }[] }[] }
      | undefined;
    if (!data) return undefined;
    const names = new Set<string>();
    if (Array.isArray(data.frames)) {
      for (const f of data.frames as { filename?: unknown }[]) if (typeof f.filename === 'string') names.add(f.filename);
    } else if (data.frames && typeof data.frames === 'object') {
      for (const k of Object.keys(data.frames)) names.add(k);
    }
    for (const t of data.textures ?? []) for (const f of t.frames ?? []) if (typeof f.filename === 'string') names.add(f.filename);
    return names;
  }

  loadAnimations(path: string, from: string) {
    const data = this.readJson(path, from, 'Animation file') as { anims?: unknown } | undefined;
    if (!data) return;
    if (!Array.isArray(data.anims)) {
      this.error(path, 'Animation files hold { "anims": [...] }, in the shape Phaser\'s anims.toJSON() writes');
      return;
    }
    (data.anims as { key?: unknown; frames?: unknown }[]).forEach((anim, i) => {
      if (typeof anim.key !== 'string') {
        this.error(path, `Animation ${i} has no key`, `/anims/${i}`);
        return;
      }
      const other = this.animationKeys.get(anim.key);
      if (other && other !== path) this.warn(path, `Animation "${anim.key}" is also defined in ${other}; the first one loaded wins`, anim.key);
      else this.animationKeys.set(anim.key, path);
      if (!Array.isArray(anim.frames)) return;
      for (const frame of anim.frames as { key?: unknown; frame?: unknown }[]) {
        if (typeof frame.key !== 'string') continue;
        const tex = this.textures.get(frame.key);
        if (!tex) {
          this.error(path, `Animation "${anim.key}" uses texture "${frame.key}", which no asset pack defines. Phaser skips frames whose texture is missing.`, anim.key);
          continue;
        }
        const names = typeof frame.frame === 'string' ? this.frameNames(tex) : undefined;
        if (names && !names.has(frame.frame as string)) {
          this.error(path, `Animation "${anim.key}" uses frame "${String(frame.frame)}", which texture "${frame.key}" doesn't have`, anim.key);
        }
      }
    });
  }

  private clipsFiles = new Map<string, ClipsFile | null>();

  /** Reads and checks a clips file once. */
  loadClips(path: string, from: string): ClipsFile | undefined {
    if (this.clipsFiles.has(path)) return this.clipsFiles.get(path) ?? undefined;
    this.clipsFiles.set(path, null);
    const value = this.readJson(path, from, 'Clips file');
    if (value === undefined || !this.schema(path, validators().clips, value)) return undefined;
    const file = this.versioned<ClipsFile>(path, value);
    if (!file) return undefined;
    this.clipsFiles.set(path, file);
    for (const [name, clip] of Object.entries(file.clips)) {
      for (const track of clip.tracks ?? []) {
        const times = isFieldTrack(track) ? track.keys : isPlayTrack(track) ? track.play : isEventTrack(track) ? track.events : isSoundTrack(track) ? track.sounds : [];
        if (isSoundTrack(track)) for (const k of track.sounds) this.checkSoundName(path, k.sound, `Clip "${name}"`, name);
        if (times.some((k) => k.t > clip.duration)) this.warn(path, `Clip "${name}" has keys after its end (${clip.duration} ms); they never play`, name);
        if (isFieldTrack(track) && !track.script && !(track.field in CLIP_FIELDS)) {
          this.warn(path, `Clip "${name}" animates "${track.field}", which clips can't animate on core objects. Script fields name their script.`, name);
        }
        if (isPlayTrack(track)) {
          for (const k of track.play) {
            if (k.anim && !this.animationKeys.has(k.anim)) this.warn(path, `Clip "${name}" plays animation "${k.anim}", which no animation file defines`, name);
          }
        }
      }
    }
    return file;
  }

  /** Checks an object's `clips`: the file, its autoplay clip, and that track targets name objects under it. */
  checkClipsRef(file: string, o: SceneObject) {
    const ref = o.clips;
    if (!ref || typeof ref !== 'object') return;
    const path = this.path(file, ref.file, o.id);
    if (!path) return;
    const clips = this.loadClips(path, file);
    if (!clips) return;
    if (ref.autoplay !== undefined && !(ref.autoplay in clips.clips)) {
      this.error(file, `Autoplay clip "${ref.autoplay}" is not in ${path}`, o.id);
    }
    // Targets inside a placed prefab are checked against the prefab; here only children written in this file.
    if (o.type === 'prefab') return;
    const has = (names: string[]) => {
      let level: SceneObject[] | undefined = [o];
      for (const n of names) {
        level = level?.flatMap((x) => ((x as { children?: SceneObject[] }).children ?? [])).filter((x) => x.name === n);
        if (!level?.length) return false;
      }
      return true;
    };
    const missing = new Set<string>();
    for (const clip of Object.values(clips.clips)) {
      for (const track of clip.tracks ?? []) {
        const names = targetPath(track.target);
        if (names.length && !has(names)) missing.add(names.join('/'));
      }
    }
    for (const m of missing) this.warn(file, `Clips in ${path} animate "${m}", which is not an object under ${o.name ?? o.id}`, o.id);
  }

  /** The project's sounds file and its data, once read; null data when it is missing or invalid. */
  sounds: { path: string; file: SoundsFile | null } | undefined;

  /** Reads and checks the sounds file once: its schema, packs (preload and loadLater), and that each sound's keys are audio in a pack. A missing file is only a problem when something plays a sound. */
  loadSounds(path: string) {
    this.sounds = { path, file: null };
    if (this.input.readFile(path) === undefined) return;
    const value = this.readJson(path, 'project.json', 'Sounds file');
    if (value === undefined || !this.schema(path, validators().sounds, value)) return;
    const file = this.versioned<SoundsFile>(path, value);
    if (!file) return;
    this.sounds.file = file;
    for (const p of [...(file.preload ?? []), ...(file.loadLater ?? [])]) {
      const pack = this.path(path, p);
      if (pack) this.loadPack(pack, path);
    }
  }

  /** Once every pack is loaded: sounds whose keys are in no asset pack's audio. */
  checkSoundKeys() {
    const file = this.sounds?.file;
    if (!file) return;
    for (const [name, def] of Object.entries(file.sounds ?? {})) {
      if (!def.keys.length) this.warn(this.sounds!.path, `Sound "${name}" has no audio to play`, name);
      for (const key of def.keys) if (!this.audio.has(key)) this.warn(this.sounds!.path, `Sound "${name}" plays "${key}", which is not audio in any asset pack`, name);
    }
  }

  /** Warns when something names a sound the sounds file lacks. */
  checkSoundName(file: string, name: string, who: string, at?: string) {
    if (!this.sounds) return;
    if (!this.sounds.file) this.warn(file, `${who} plays the sound "${name}", but ${this.sounds.path} is missing or invalid`, at);
    else if (!this.sounds.file.sounds?.[name]) this.warn(file, `${who} plays the sound "${name}", which is not in ${this.sounds.path}`, at);
  }

  /** The project's theme file and its data, once read; null data when it is missing or invalid. */
  theme: { path: string; file: ThemeFile | null } | undefined;

  /** Reads and checks the theme file once. A missing theme is only a problem when objects use it. */
  loadTheme(path: string): ThemeFile | undefined {
    if (this.theme) return this.theme.file ?? undefined;
    this.theme = { path, file: null };
    const text = this.input.readFile(path);
    if (text === undefined) return undefined;
    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch (e) {
      this.error(path, `Not valid JSON: ${(e as Error).message}`);
      return undefined;
    }
    if (!this.schema(path, validators().theme, value)) return undefined;
    const theme = this.versioned<ThemeFile>(path, value);
    if (!theme) return undefined;
    this.theme.file = theme;
    for (const p of checkThemeRefs(path, theme)) this.problems.push(p);
    for (const [name, font] of Object.entries(theme.fonts ?? {})) {
      for (const key of ['url', 'bitmap', 'texture'] as const) {
        const file = font[key] && this.path(path, font[key], name);
        if (file && this.input.readFile(file) === undefined) this.error(path, `Font "${name}" ${key === 'url' ? 'file' : key === 'bitmap' ? 'data' : 'texture'} ${file} does not exist`, name);
      }
      if ((font.bitmap === undefined) !== (font.texture === undefined)) this.error(path, `Bitmap font "${name}" needs both its data file (bitmap) and its texture`, name);
    }
    return theme;
  }

  /** Checks that an object's `themeStyle` names a style of the right kind. */
  checkThemeStyle(file: string, o: SceneObject) {
    const name = (o as { themeStyle?: unknown }).themeStyle;
    if (typeof name !== 'string' || !this.theme) return;
    const section = styleSectionFor(o.type);
    if (!section) return;
    const theme = this.theme.file;
    if (!theme) {
      this.warn(file, `Uses theme style "${name}", but ${this.theme.path} is missing or invalid`, o.id);
    } else if (!(theme[section] as Record<string, unknown> | undefined)?.[name]) {
      this.warn(file, `Theme style "${name}" is not in ${this.theme.path}'s ${section}`, o.id);
    }
  }

  getPrefab = (path: string): PrefabFile | undefined => {
    if (this.prefabs.has(path)) return this.prefabs.get(path) ?? undefined;
    this.prefabs.set(path, null);
    let safe: string;
    try {
      safe = normalizeProjectPath(path);
    } catch {
      return undefined;
    }
    const text = this.input.readFile(safe);
    if (text === undefined) return undefined;
    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch (e) {
      this.error(safe, `Not valid JSON: ${(e as Error).message}`);
      return undefined;
    }
    if (!this.schema(safe, validators().prefab, value)) return undefined;
    const prefab = this.versioned<PrefabFile>(safe, value);
    if (!prefab) return undefined;
    this.prefabs.set(path, prefab);
    this.checkIds(safe, [prefab.root]);
    for (const cycle of findPrefabCycles(this.getPrefab, safe)) {
      if (cycle[0] === safe) this.error(safe, `Prefab contains itself: ${cycle.join(' → ')}`);
    }
    return prefab;
  };

  checkIds(file: string, objects: SceneObject[] | undefined) {
    const seen = new Set<string>();
    for (const id of listIds(objects)) {
      if (seen.has(id)) this.error(file, `Id "${id}" is used by more than one object`, id);
      seen.add(id);
    }
  }

  /** A scene's or prefab's tilemaps list: unique ids, and maps that load. */
  checkMaps(file: string, tilemaps: SceneTilemapRef[] | undefined) {
    const ids = new Set<string>();
    for (const t of tilemaps ?? []) {
      if (ids.has(t.id)) this.error(file, `Tilemap id "${t.id}" is listed twice`);
      ids.add(t.id);
      const map = this.path(file, t.file);
      if (map) this.tilemapLayers(map, file);
    }
  }

  /** Plugin data, plugin settings and script fields that name a file which isn't there, such as a kit's room after a move by hand. */
  checkDataLinks(file: string, value: unknown, at?: string) {
    for (const { raw } of dataFileLinks(value)) {
      let path: string;
      try {
        path = normalizeProjectPath(raw);
      } catch {
        continue;
      }
      if (this.input.readFile(path) === undefined) this.warn(file, `"${raw}" names a file that does not exist`, at);
    }
  }

  checkObjects(file: string, objects: SceneObject[] | undefined, scene: SceneFile | undefined, preloaded: Set<string>) {
    const ids = new Set(listIds(objects));
    walkObjects(objects, (o) => {
      for (const c of readConnections(o.connections)) {
        if (c.to && c.to !== CAMERA_TARGET && !ids.has(c.to)) this.error(file, `A connection on "${o.name ?? o.id}" points at ${c.to}, which isn't in this file`, o.id);
        if (CONNECTION_ACTIONS[c.do]?.args?.[0]?.type === 'sound' && typeof c.args?.[0] === 'string' && c.args[0]) this.checkSoundName(file, c.args[0], `A connection on "${o.name ?? o.id}"`, o.id);
      }
      if (isCoreType(o.type)) {
        const allowed = new Set<string>([...COMMON_FIELDS, ...DISPLAY_FIELDS, ...TYPE_FIELDS[o.type]]);
        for (const k of Object.keys(o)) {
          if (!allowed.has(k)) this.warn(file, `"${k}" is not a field of ${o.type} objects and is ignored`, o.id);
        }
      }
      if (o.scripts && typeof o.scripts === 'object' && this.input.scriptExists) {
        for (const [script, values] of Object.entries(o.scripts)) {
          if (values !== null && !isPluginScript(script) && !this.input.scriptExists(script)) this.warn(file, `Script "${script}" was not found`, o.id);
        }
      }
      this.checkDataLinks(file, [o.data, o.scripts], o.id);
      this.checkThemeStyle(file, o);
      this.checkClipsRef(file, o);
      const texture = (o as SpriteObject).texture;
      if (texture && typeof texture.key === 'string') {
        const tex = this.textures.get(texture.key);
        if (!tex) {
          this.error(file, `Texture "${texture.key}" is not in any asset pack`, o.id);
        } else {
          if (scene && !preloaded.has(tex.pack)) {
            this.warn(file, `Texture "${texture.key}" comes from ${tex.pack}, which this scene doesn't preload`, o.id);
          }
          if (o.type === 'vector') this.checkVectorTexture(file, o.id, tex);
          const names = typeof texture.frame === 'string' ? this.frameNames(tex) : undefined;
          if (names && !names.has(texture.frame as string)) {
            this.error(file, `Texture "${texture.key}" has no frame "${String(texture.frame)}"`, o.id);
          }
        }
      }
      const font = (o as BitmapTextObject).font;
      if (o.type === 'bitmapText' && typeof font === 'string') {
        const entry = this.fonts.get(font);
        // The theme's bitmap fonts load with the theme, under their family name.
        const themeFont = this.theme?.file?.fonts?.[font];
        const fromTheme = !!(themeFont?.bitmap && themeFont.texture);
        if (!entry && !fromTheme) this.error(file, `Bitmap font "${font}" is not in any asset pack or the theme`, o.id);
        else if (entry && scene && !preloaded.has(entry.pack)) this.warn(file, `Bitmap font "${font}" comes from ${entry.pack}, which this scene doesn't preload`, o.id);
      }
      const anim = (o as SpriteObject).animation;
      if (o.type === 'sprite' && anim && typeof anim.key === 'string' && !this.animationKeys.has(anim.key)) {
        this.warn(file, `Animation "${anim.key}" is not defined in any animation file or Aseprite asset`, o.id);
      }
      if (o.type === 'tilemapLayer') {
        const layer = o as TilemapLayerObject;
        // A tile layer's map is in the tilemaps list of the file it is in: the scene's or the prefab's.
        const ref = (scene ?? this.getPrefab(file))?.tilemaps?.find((t) => t.id === layer.map);
        const mapFile = ref && this.path(file, ref.file);
        if (!ref) {
          this.error(file, `Tile layer uses map "${layer.map}", which is not in the ${scene ? 'scene' : 'prefab'}'s tilemaps list`, o.id);
        } else if (mapFile) {
          const names = this.tilemapLayers(mapFile, file);
          if (names && !names.includes(layer.layer)) {
            this.error(file, `Tilemap ${ref.file} has no tile layer "${layer.layer}"`, o.id);
          }
        }
      }
      if (o.type === 'prefab') this.path(file, (o as { prefab?: unknown }).prefab, o.id);
      if (o.type === 'video') {
        const key = (o as VideoObject).key;
        if (typeof key === 'string' && !this.videos.has(key)) this.error(file, `Video "${key}" is not in any asset pack`, o.id);
      }
      this.checkFilters(file, (o as { filters?: unknown }).filters, objects, o.id);
    });
  }

  /** Mask filters must name an object in the same file, or a texture some pack loads. */
  checkFilters(file: string, filters: unknown, objects: SceneObject[] | undefined, at?: string) {
    if (!Array.isArray(filters)) return;
    for (const f of filters as FilterEntry[]) {
      if (f?.type !== 'mask') continue;
      if (f.object !== undefined && !listIds(objects).includes(f.object)) {
        this.error(file, `Mask filter uses object "${f.object}", which is not in this file`, at);
      }
      if (f.texture !== undefined && !this.textures.has(f.texture)) {
        this.error(file, `Mask filter uses texture "${f.texture}", which is not in any asset pack`, at);
      }
      if (f.object === undefined && f.texture === undefined) this.warn(file, 'Mask filter has no object or texture, so it does nothing', at);
    }
  }

  private tilemapCache = new Map<string, string[] | null>();

  /** Checks a Tiled file and returns its tile layer names (with group prefixes). */
  tilemapLayers(path: string, from: string): string[] | undefined {
    if (this.tilemapCache.has(path)) return this.tilemapCache.get(path) ?? undefined;
    this.tilemapCache.set(path, null);
    const map = this.readJson(path, from, 'Tilemap') as
      | { layers?: unknown; tilesets?: unknown; orientation?: unknown }
      | undefined;
    if (!map) return undefined;
    if (!Array.isArray(map.layers)) {
      this.error(path, 'Not a Tiled JSON map: no layers array');
      return undefined;
    }
    for (const ts of (Array.isArray(map.tilesets) ? map.tilesets : []) as Record<string, unknown>[]) {
      if (typeof ts.source === 'string') {
        this.error(path, `Tileset "${ts.source}" is an external file. Phaser can't load external tilesets; embed it.`);
        continue;
      }
      if (typeof ts.image === 'string') {
        let image: string | undefined;
        try {
          image = resolveFrom(path, ts.image);
        } catch (e) {
          this.error(path, (e as Error).message, String(ts.name ?? ''));
        }
        if (image && this.input.readFile(image) === undefined) {
          this.error(path, `Tileset "${String(ts.name)}" image ${image} does not exist`);
        }
      }
    }
    const names: string[] = [];
    const walk = (layers: Record<string, unknown>[], prefix: string) => {
      for (const layer of layers) {
        const name = prefix + String(layer.name ?? '');
        if (layer.type === 'group' && Array.isArray(layer.layers)) {
          walk(layer.layers as Record<string, unknown>[], name + '/');
        } else if (layer.type === 'tilelayer') {
          names.push(name);
          if (layer.compression || layer.encoding === 'base64') {
            this.error(path, `Layer "${name}" uses ${String(layer.compression || 'base64')} tile data. Phaser needs plain tile arrays; save it uncompressed as CSV/JSON.`);
          }
          if (Array.isArray(layer.chunks)) {
            this.error(path, `Layer "${name}" is an infinite map with chunks, which Phaser doesn't load`);
          }
        }
      }
    };
    walk(map.layers as Record<string, unknown>[], '');
    this.tilemapCache.set(path, names);
    return names;
  }
}

/** Validates a whole project. Returns every problem found, errors and warnings. */
export function validateProject(input: ValidateInput): Problem[] {
  const c = new Checker(input);
  const v = validators();
  if (!c.schema('project.json', v.project, input.project)) return c.problems;
  const project = c.versioned<ProjectFile>('project.json', input.project);
  if (!project) return c.problems;
  const themePath = c.path('project.json', themePathOf(project));
  if (themePath) c.loadTheme(themePath);
  const soundsPath = c.path('project.json', soundsPathOf(project));
  if (soundsPath) c.loadSounds(soundsPath);
  if (!project.sounds && soundsPath && input.readFile(soundsPath) !== undefined) {
    c.warn('project.json', `${soundsPath} is there, but project.json has no "sounds", so the game won't load it. Add "sounds": "${soundsPath}"`, 'sounds');
  }
  c.checkDataLinks('project.json', project.settings, 'settings');
  const actionNames = new Set<string>();
  for (const a of project.input?.actions ?? []) {
    if (actionNames.has(a.name)) c.error('project.json', `Input action "${a.name}" is listed twice`, 'input');
    actionNames.add(a.name);
  }

  const scenes: { path: string; scene: SceneFile }[] = [];
  for (const raw of project.scenes ?? []) {
    const path = c.path('project.json', raw);
    if (!path) continue;
    const value = c.readJson(path, 'project.json', 'Scene');
    if (value === undefined || !c.schema(path, v.scene, value)) continue;
    const scene = c.versioned<SceneFile>(path, value);
    if (!scene) continue;
    const other = c.sceneKeys.get(scene.key);
    if (other) c.error(path, `Scene key "${scene.key}" is also used by ${other}`);
    c.sceneKeys.set(scene.key, path);
    scenes.push({ path, scene });
  }

  // Load every pack and animation first, so object checks see all keys.
  for (const { path, scene } of scenes) {
    for (const p of scene.preload ?? []) {
      const pack = c.path(path, p);
      if (pack) c.loadPack(pack, path);
    }
  }
  c.checkSoundKeys();
  const animFiles = new Set<string>();
  for (const { path, scene } of scenes) {
    for (const a of scene.animations ?? []) {
      const file = c.path(path, a);
      if (file && !animFiles.has(file)) {
        animFiles.add(file);
        c.loadAnimations(file, path);
      }
    }
  }

  const checkedPrefabs = new Set<string>();
  const checkPrefab = (path: string) => {
    if (checkedPrefabs.has(path)) return;
    checkedPrefabs.add(path);
    const prefab = c.getPrefab(path);
    if (!prefab) return;
    c.checkMaps(path, prefab.tilemaps);
    c.checkObjects(path, [prefab.root], undefined, new Set());
    walkObjects([prefab.root], (o) => {
      if (o.type === 'prefab') {
        const p = c.path(path, (o as { prefab?: unknown }).prefab);
        if (p) checkPrefab(p);
      }
    });
  };

  for (const { path, scene } of scenes) {
    c.checkIds(path, scene.children);
    const preloaded = new Set((scene.preload ?? []).map((p) => c.path(path, p)).filter((p): p is string => !!p));
    c.checkMaps(path, scene.tilemaps);
    c.checkDataLinks(path, scene.data);
    c.checkObjects(path, scene.children, scene, preloaded);
    if (scene.camera?.follow !== undefined && !listIds(scene.children).includes(scene.camera.follow)) {
      c.error(path, `The camera follows "${scene.camera.follow}", which is not an object in this scene`);
    }
    c.checkFilters(path, scene.camera?.filters, scene.children);
    for (const name of sceneSoundNames(scene.sound)) c.checkSoundName(path, name, 'The scene');
    walkObjects(scene.children, (o) => {
      if (o.type !== 'prefab') return;
      const p = c.path(path, (o as { prefab?: unknown }).prefab);
      if (!p) return;
      if (input.readFile(p) === undefined) {
        c.error(path, `Prefab "${p}" does not exist`, o.id);
        return;
      }
      checkPrefab(p);
    });
    resolveObjects(scene.children, {
      file: path,
      getPrefab: (p) => c.getPrefab(p),
      onWarning: (w) => {
        // Missing prefabs and cycles are already reported as errors above.
        if (w.message.startsWith('Prefab cycle') || w.message.includes('was not found')) return;
        c.warn(w.file, w.message, w.id);
      },
    });
  }
  for (const p of input.prefabs ?? []) {
    const path = c.path('project.json', p);
    if (path) checkPrefab(path);
  }

  // Deduplicate: shared files can be reached from several scenes.
  const seen = new Set<string>();
  return c.problems.filter((p) => {
    const key = `${p.severity}|${p.file}|${p.at ?? ''}|${p.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export { CORE_TYPES };

/**
 * Checks one project, scene, prefab, clips or theme file on its own: its
 * schema and unique ids. The dev server runs this on every file a command
 * touches and refuses commands that would leave a file invalid. Other files
 * return no problems. `themePath` and `soundsPath` are the project's theme and
 * sounds files, if not the defaults.
 */
export function checkFile(path: string, value: unknown, themePath = themePathOf(undefined), soundsPath = soundsPathOf(undefined)): Problem[] {
  const kind = fileKindFor(path);
  if (path.endsWith('.anims.json')) return checkAnimsShape(path, value);
  const c = new Checker({ project: undefined, readFile: () => undefined });
  const v = validators();
  if (path.endsWith('.clips.json')) {
    c.schema(path, v.clips, value);
    return c.problems;
  }
  if (path === themePath) {
    c.schema(path, v.theme, value);
    return c.problems;
  }
  if (path === soundsPath) {
    c.schema(path, v.sounds, value);
    return c.problems;
  }
  if (kind === 'json') return [];
  if (!c.schema(path, v[kind], value)) return c.problems;
  if (kind === 'scene') c.checkIds(path, (value as SceneFile).children);
  if (kind === 'prefab') c.checkIds(path, [(value as PrefabFile).root]);
  return c.problems.filter((p) => p.severity === 'error');
}

/**
 * Checks the names a theme uses inside itself: colours that are neither a
 * hex value nor a theme colour, and styles naming styles the theme lacks.
 * Warnings only; the runtime falls back to Phaser's defaults.
 */
export function checkThemeRefs(path: string, theme: ThemeFile): Problem[] {
  const problems: Problem[] = [];
  const warn = (message: string, at: string) => problems.push({ severity: 'warning', file: path, message, at });
  for (const section of THEME_SECTIONS) {
    if (section === 'colors' || section === 'fonts') continue;
    for (const [name, style] of Object.entries((theme[section] ?? {}) as Record<string, unknown>)) {
      for (const ref of colorRefs(section, style)) {
        if (!isHexColor(ref.value) && !theme.colors?.[ref.value]) warn(`"${name}" uses colour "${ref.value}", which is neither a theme colour nor a #hex value`, name);
      }
      for (const ref of styleRefs(section, style)) {
        // A font the theme doesn't list can still be one the system has.
        if (ref.section === 'fonts') continue;
        if (!(theme[ref.section] as Record<string, unknown> | undefined)?.[ref.value]) warn(`"${name}" uses ${ref.section === 'text' ? 'text style' : 'panel'} "${ref.value}", which the theme doesn't have`, name);
      }
    }
  }
  const sections = { text: 'text', button: 'buttons', panel: 'panels', bar: 'bars' } as const;
  for (const [kind, name] of Object.entries(theme.defaults ?? {})) {
    const section = sections[kind as keyof typeof sections];
    if (name && !(theme[section] as Record<string, unknown> | undefined)?.[name]) warn(`The default ${kind} style "${name}" is not in the theme`, `defaults/${kind}`);
  }
  return problems;
}

/** The shape an animation file must keep for Phaser's anims.fromJSON to load it. */
function checkAnimsShape(path: string, value: unknown): Problem[] {
  const problems: Problem[] = [];
  const error = (message: string, at?: string) => problems.push({ severity: 'error', file: path, message, ...(at !== undefined && { at }) });
  const anims = (value as { anims?: unknown } | null)?.anims;
  if (!Array.isArray(anims)) {
    error('Animation files hold { "anims": [...] }, in the shape Phaser\'s anims.toJSON() writes');
    return problems;
  }
  const seen = new Set<string>();
  anims.forEach((anim: { key?: unknown; frames?: unknown } | null, i) => {
    if (typeof anim?.key !== 'string' || !anim.key) return error(`Animation ${i} has no key`, `/anims/${i}`);
    if (seen.has(anim.key)) error(`Two animations are called "${anim.key}"`, `/anims/${i}/key`);
    seen.add(anim.key);
    if (!Array.isArray(anim.frames)) return error(`Animation "${anim.key}" has no frames list`, `/anims/${i}/frames`);
    (anim.frames as { key?: unknown }[]).forEach((f, j) => {
      if (typeof f?.key !== 'string') error(`Frame ${j} of "${anim.key}" has no texture key`, `/anims/${i}/frames/${j}`);
    });
  });
  return problems;
}
